import { GRStatus, POStatus } from '@prisma/client';
import { syncGRToDatabase } from './dbBridge';
import {
  poDatabase,
  PORecord,
  POItemRecord,
  registerPOIssuedListener,
  registerPOCancelListener,
  registerPOCompletedListener,
} from './mockPoStore';
import { MASTER_ITEMS, addAuditLog, auditLogsDatabase, PRAuditRecord } from './mockPrStore';
import { validateGROverDelivery, calculatePOReceiptStatus } from '../services/grService';
import { loadPersistentData, savePersistentData } from './fileStorage';

export interface GoodsReceiptItemRecord {
  id: string;
  grId: string;
  poItemId: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  receivedQty: number; // Good quality quantity (increases inventory)
  rejectedQty: number; // Damaged / rejected quantity (vendor quality audit)
  uomId: string;
  uomCode: string;
  unitCost: number; // Historical PO unit price
  conditionNotes?: string;
  inventoryTxId?: string;
  costLayerId?: string;
  createdAt: string;
}

export interface GRDocumentAttachment {
  id: string;
  customName: string;
  originalFileName: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
}

export interface GoodsReceiptRecord {
  id: string;
  grNumber: string;
  vendorDeliveryNote: string;
  receiveDate: string;
  poId: string;
  poNumber: string;
  supplierId: string;
  supplierName: string;
  receiverId: string;
  receiverName: string;
  status: GRStatus;
  notes?: string;
  documentFileName?: string;
  documentFilePath?: string;
  documentFileType?: string;
  documentFileSize?: number;
  documentUploadedAt?: string;
  documents?: GRDocumentAttachment[];
  items: GoodsReceiptItemRecord[];
  totalGoodQty: number;
  totalRejectedQty: number;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryTransactionRecord {
  id: string;
  grId: string;
  grNumber: string;
  poId: string;
  poNumber: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  quantity: number; // Positive quantity added to stock
  uomCode: string;
  txType: 'PURCHASE_RECEIPT' | 'ADJUSTMENT';
  txDate: string;
  createdAt: string;
}

export interface InventoryCostLayerRecord {
  id: string; // Unique ID / Key
  inventoryId: string; // Distinct Inventory ID with epoch millisecond format: "INV-<epochtimestampms>"
  grItemId: string;
  grId: string;
  grNumber: string;
  poId: string;
  poNumber: string;
  prNumber?: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  category?: string;
  condition: string; // e.g. "Grade A (QC Passed)", "Grade B (Kondisi Khusus - Retak Ringan)"
  conditionNotes?: string;
  batchNumber: string;
  warehouseLocation: string;
  status: 'AVAILABLE' | 'RESERVED' | 'CONSUMED' | 'QUARANTINE';
  initialQty: number;
  remainingQty: number;
  unitCost: number;
  uomCode: string;
  supplierName?: string;
  vendorDeliveryNote?: string;
  receiverName?: string;
  createdAt: string;
}

export interface ItemStockSummaryRecord {
  itemId: string;
  itemCode: string;
  itemName: string;
  category: string;
  uomCode: string;
  currentStock: number;
  totalValue: number;
  layerCount: number;
}

export interface ReceiveGoodsItemInput {
  poItemId: string;
  goodReceivedQty?: number;
  receivedQty?: number; // alias for goodReceivedQty
  rejectedQty?: number;
  conditionNotes?: string;
}

export interface ReceiveGoodsPayload {
  poId: string;
  vendorDeliveryNote: string;
  receiveDate?: string;
  notes?: string;
  items: ReceiveGoodsItemInput[];
}

// Memory databases
const initialGrDatabase: GoodsReceiptRecord[] = [
  {
    id: 'gr-001',
    grNumber: 'GR-2026-09-001',
    vendorDeliveryNote: 'SJ/SLM/2026/09/101',
    receiveDate: '2026-09-13T16:00:00.000Z',
    poId: 'po-002',
    poNumber: 'PO-2026-09-002',
    supplierId: 'sup-002',
    supplierName: 'CV Sumber Logistik Mandiri',
    receiverId: 'usr-wh-01',
    receiverName: 'Agus Wibowo',
    status: GRStatus.CONFIRMED,
    notes: 'Initial partial shipment for PO-2026-09-002. Inspected at unloading dock.',
    totalGoodQty: 120,
    totalRejectedQty: 1,
    items: [
      {
        id: 'gri-001-1',
        grId: 'gr-001',
        poItemId: 'poi-002-1',
        itemId: 'itm-005',
        itemCode: 'ITM-005',
        itemName: 'Standard Wooden Pallet 1200x1000mm',
        receivedQty: 20,
        rejectedQty: 1,
        uomId: 'uom-001',
        uomCode: 'PCS',
        unitCost: 225000,
        conditionNotes: '1 pallet had cracked deck board, rejected at gate and flagged for vendor credit.',
        inventoryTxId: 'itx-001',
        costLayerId: 'icl-001',
        createdAt: '2026-09-13T16:00:00.000Z',
      },
      {
        id: 'gri-001-2',
        grId: 'gr-001',
        poItemId: 'poi-002-2',
        itemId: 'itm-004',
        itemCode: 'ITM-004',
        itemName: 'Industrial Opp Tape 2 inch Clear',
        receivedQty: 100,
        rejectedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        unitCost: 12500,
        conditionNotes: 'Full carton tape verified in pristine condition.',
        inventoryTxId: 'itx-002',
        costLayerId: 'icl-002',
        createdAt: '2026-09-13T16:00:00.000Z',
      },
    ],
    createdAt: '2026-09-13T16:00:00.000Z',
    updatedAt: '2026-09-13T16:00:00.000Z',
  },
  {
    id: 'gr-002',
    grNumber: 'GR-2026-09-002',
    vendorDeliveryNote: 'SJ/MSI/2026/09/088',
    receiveDate: '2026-09-15T11:00:00.000Z',
    poId: 'po-003',
    poNumber: 'PO-2026-09-003',
    supplierId: 'sup-003',
    supplierName: 'PT Mitra Sukses Industri',
    receiverId: 'usr-wh-01',
    receiverName: 'Agus Wibowo',
    status: GRStatus.CONFIRMED,
    notes: 'Penerimaan lengkap 100% bahan baku denim & aksesoris jaket. Pemeriksaan visual kain & aksesoris lolos QC Grade A.',
    totalGoodQty: 4400,
    totalRejectedQty: 0,
    items: [
      {
        id: 'gri-002-1',
        grId: 'gr-002',
        poItemId: 'poi-003-1',
        itemId: 'itm-011',
        itemCode: 'ITM-011',
        itemName: 'Kain Denim Stretch 12oz Indigo',
        receivedQty: 800,
        rejectedQty: 0,
        uomId: 'uom-003',
        uomCode: 'METER',
        unitCost: 45000,
        conditionNotes: 'Roll kain denim stretch 12oz utuh rapi terbungkus plastik, sertifikat grade ekspor',
        inventoryTxId: 'itx-003',
        costLayerId: 'icl-003',
        createdAt: '2026-09-15T11:00:00.000Z',
      },
      {
        id: 'gri-002-2',
        grId: 'gr-002',
        poItemId: 'poi-003-2',
        itemId: 'itm-010',
        itemCode: 'ITM-010',
        itemName: 'Resleting / Zipper YKK Metal 5 inch',
        receivedQty: 600,
        rejectedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        unitCost: 4200,
        conditionNotes: 'Resleting YKK metal kuningan antik 5 inch tersegel kardus isi 600 pcs',
        inventoryTxId: 'itx-004',
        costLayerId: 'icl-004',
        createdAt: '2026-09-15T11:00:00.000Z',
      },
      {
        id: 'gri-002-3',
        grId: 'gr-002',
        poItemId: 'poi-003-3',
        itemId: 'itm-014',
        itemCode: 'ITM-014',
        itemName: 'Label Woven Satin Brand PT XYZ',
        receivedQty: 3000,
        rejectedQty: 0,
        uomId: 'uom-001',
        uomCode: 'PCS',
        unitCost: 350,
        conditionNotes: 'Label woven satin damask 25x55mm, 3 roll x 1000 pcs',
        inventoryTxId: 'itx-005',
        costLayerId: 'icl-005',
        createdAt: '2026-09-15T11:00:00.000Z',
      },
    ],
    createdAt: '2026-09-15T11:00:00.000Z',
    updatedAt: '2026-09-15T11:00:00.000Z',
  },
  {
    id: 'gr-003',
    grNumber: 'GR-2026-09-003',
    vendorDeliveryNote: 'SJ/SLM/2026/09/145',
    receiveDate: '2026-09-15T15:00:00.000Z',
    poId: 'po-004',
    poNumber: 'PO-2026-09-004',
    supplierId: 'sup-002',
    supplierName: 'CV Sumber Logistik Mandiri',
    receiverId: 'usr-wh-01',
    receiverName: 'Agus Wibowo',
    status: GRStatus.CONFIRMED,
    notes: 'Penerimaan jarum jahit dan sarung tangan kerja. 2 pasang sarung tangan cacat sobek jahitan disisihkan untuk klaim retur vendor.',
    totalGoodQty: 125,
    totalRejectedQty: 2,
    items: [
      {
        id: 'gri-003-1',
        grId: 'gr-003',
        poItemId: 'poi-004-1',
        itemId: 'itm-016',
        itemCode: 'ITM-016',
        itemName: 'Jarum Mesin Jahit Groz-Beckert DPx5 #14',
        receivedQty: 25,
        rejectedQty: 0,
        uomId: 'uom-005',
        uomCode: 'BOX',
        unitCost: 85000,
        conditionNotes: 'Segel Groz-Beckert original utuh, 25 box lolos inspeksi',
        inventoryTxId: 'itx-006',
        costLayerId: 'icl-006',
        createdAt: '2026-09-15T15:00:00.000Z',
      },
      {
        id: 'gri-003-2',
        grId: 'gr-003',
        poItemId: 'poi-004-2',
        itemId: 'itm-019',
        itemCode: 'ITM-019',
        itemName: 'Sarung Tangan Kerja Katun Rajut Bintik',
        receivedQty: 100,
        rejectedQty: 2,
        uomId: 'uom-001',
        uomCode: 'PASANG',
        unitCost: 6500,
        conditionNotes: '100 pasang baik lolos QC. 2 pasang cacat robek pada pergelangan tangan (reject log vendor).',
        inventoryTxId: 'itx-007',
        costLayerId: 'icl-007',
        createdAt: '2026-09-15T15:00:00.000Z',
      },
    ],
    createdAt: '2026-09-15T15:00:00.000Z',
    updatedAt: '2026-09-15T15:00:00.000Z',
  },
];

export const grDatabase: GoodsReceiptRecord[] = loadPersistentData('gr-database.json', initialGrDatabase);

export const inventoryTransactionDatabase: InventoryTransactionRecord[] = [
  {
    id: 'itx-001',
    grId: 'gr-001',
    grNumber: 'GR-2026-09-001',
    poId: 'po-002',
    poNumber: 'PO-2026-09-002',
    itemId: 'itm-005',
    itemCode: 'ITM-005',
    itemName: 'Standard Wooden Pallet 1200x1000mm',
    quantity: 20,
    uomCode: 'PCS',
    txType: 'PURCHASE_RECEIPT',
    txDate: '2026-09-13T16:00:00.000Z',
    createdAt: '2026-09-13T16:00:00.000Z',
  },
  {
    id: 'itx-002',
    grId: 'gr-001',
    grNumber: 'GR-2026-09-001',
    poId: 'po-002',
    poNumber: 'PO-2026-09-002',
    itemId: 'itm-004',
    itemCode: 'ITM-004',
    itemName: 'Industrial Opp Tape 2 inch Clear',
    quantity: 100,
    uomCode: 'PCS',
    txType: 'PURCHASE_RECEIPT',
    txDate: '2026-09-13T16:00:00.000Z',
    createdAt: '2026-09-13T16:00:00.000Z',
  },
  {
    id: 'itx-003',
    grId: 'gr-002',
    grNumber: 'GR-2026-09-002',
    poId: 'po-003',
    poNumber: 'PO-2026-09-003',
    itemId: 'itm-011',
    itemCode: 'ITM-011',
    itemName: 'Kain Denim Stretch 12oz Indigo',
    quantity: 800,
    uomCode: 'METER',
    txType: 'PURCHASE_RECEIPT',
    txDate: '2026-09-15T11:00:00.000Z',
    createdAt: '2026-09-15T11:00:00.000Z',
  },
  {
    id: 'itx-004',
    grId: 'gr-002',
    grNumber: 'GR-2026-09-002',
    poId: 'po-003',
    poNumber: 'PO-2026-09-003',
    itemId: 'itm-010',
    itemCode: 'ITM-010',
    itemName: 'Resleting / Zipper YKK Metal 5 inch',
    quantity: 600,
    uomCode: 'PCS',
    txType: 'PURCHASE_RECEIPT',
    txDate: '2026-09-15T11:00:00.000Z',
    createdAt: '2026-09-15T11:00:00.000Z',
  },
  {
    id: 'itx-005',
    grId: 'gr-002',
    grNumber: 'GR-2026-09-002',
    poId: 'po-003',
    poNumber: 'PO-2026-09-003',
    itemId: 'itm-014',
    itemCode: 'ITM-014',
    itemName: 'Label Woven Satin Brand PT XYZ',
    quantity: 3000,
    uomCode: 'PCS',
    txType: 'PURCHASE_RECEIPT',
    txDate: '2026-09-15T11:00:00.000Z',
    createdAt: '2026-09-15T11:00:00.000Z',
  },
  {
    id: 'itx-006',
    grId: 'gr-003',
    grNumber: 'GR-2026-09-003',
    poId: 'po-004',
    poNumber: 'PO-2026-09-004',
    itemId: 'itm-016',
    itemCode: 'ITM-016',
    itemName: 'Jarum Mesin Jahit Groz-Beckert DPx5 #14',
    quantity: 25,
    uomCode: 'BOX',
    txType: 'PURCHASE_RECEIPT',
    txDate: '2026-09-15T15:00:00.000Z',
    createdAt: '2026-09-15T15:00:00.000Z',
  },
  {
    id: 'itx-007',
    grId: 'gr-003',
    grNumber: 'GR-2026-09-003',
    poId: 'po-004',
    poNumber: 'PO-2026-09-004',
    itemId: 'itm-019',
    itemCode: 'ITM-019',
    itemName: 'Sarung Tangan Kerja Katun Rajut Bintik',
    quantity: 100,
    uomCode: 'PASANG',
    txType: 'PURCHASE_RECEIPT',
    txDate: '2026-09-15T15:00:00.000Z',
    createdAt: '2026-09-15T15:00:00.000Z',
  },
];

export const inventoryCostLayerDatabase: InventoryCostLayerRecord[] = [
  {
    id: 'INV-1789401600000',
    inventoryId: 'INV-1789401600000',
    grItemId: 'gri-001-1',
    grId: 'gr-001',
    grNumber: 'GR-2026-09-001',
    poId: 'po-002',
    poNumber: 'PO-2026-09-002',
    prNumber: 'PR-2026-09-003',
    itemId: 'itm-005',
    itemCode: 'ITM-005',
    itemName: 'Standard Wooden Pallet 1200x1000mm',
    category: 'Warehouse Equipment',
    condition: 'Grade A (QC Passed - Sempurna)',
    conditionNotes: 'Kayu pinus kering oven, sertifikasi ISPM-15 lengkap, siap pakai',
    batchNumber: 'BATCH-202609-PL01',
    warehouseLocation: 'Gudang Utama - Rak Pallet B-01',
    status: 'AVAILABLE',
    initialQty: 20,
    remainingQty: 20,
    unitCost: 225000,
    uomCode: 'PCS',
    supplierName: 'CV Sumber Logistik Mandiri',
    vendorDeliveryNote: 'SJ/SLM/2026/09/101',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-13T16:00:00.000Z',
  },
  {
    id: 'INV-1789401601250',
    inventoryId: 'INV-1789401601250',
    grItemId: 'gri-001-2',
    grId: 'gr-001',
    grNumber: 'GR-2026-09-001',
    poId: 'po-002',
    poNumber: 'PO-2026-09-002',
    prNumber: 'PR-2026-09-003',
    itemId: 'itm-004',
    itemCode: 'ITM-004',
    itemName: 'Industrial Opp Tape 2 inch Clear',
    category: 'Consumables',
    condition: 'Grade A (Pristine Condition)',
    conditionNotes: 'Daya rekat 45 mikron prima, kemasan utuh per karton',
    batchNumber: 'BATCH-202609-TP05',
    warehouseLocation: 'Gudang Penunjang - Rak C-03',
    status: 'AVAILABLE',
    initialQty: 100,
    remainingQty: 100,
    unitCost: 12500,
    uomCode: 'PCS',
    supplierName: 'CV Sumber Logistik Mandiri',
    vendorDeliveryNote: 'SJ/SLM/2026/09/101',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-13T16:00:00.000Z',
  },
  {
    id: 'INV-1789403400500',
    inventoryId: 'INV-1789403400500',
    grItemId: 'gri-001-3',
    grId: 'gr-001',
    grNumber: 'GR-2026-09-001',
    poId: 'po-002',
    poNumber: 'PO-2026-09-002',
    prNumber: 'PR-2026-09-003',
    itemId: 'itm-005',
    itemCode: 'ITM-005',
    itemName: 'Standard Wooden Pallet 1200x1000mm',
    category: 'Warehouse Equipment',
    condition: 'Grade B (Kondisi Khusus - Retak Ringan)',
    conditionNotes: '1 unit pallet papan atas retak ringan, disimpan terpisah untuk muatan ringan non-ekspor',
    batchNumber: 'BATCH-202609-PL02-DEFECT',
    warehouseLocation: 'Gudang Karantina & Khusus - Area D-01',
    status: 'AVAILABLE',
    initialQty: 1,
    remainingQty: 1,
    unitCost: 180000,
    uomCode: 'PCS',
    supplierName: 'CV Sumber Logistik Mandiri',
    vendorDeliveryNote: 'SJ/SLM/2026/09/101',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-13T16:30:00.000Z',
  },
  {
    id: 'INV-1789500000001',
    inventoryId: 'INV-1789500000001',
    grItemId: 'gri-002-1',
    grId: 'gr-002',
    grNumber: 'GR-2026-09-002',
    poId: 'po-003',
    poNumber: 'PO-2026-09-003',
    prNumber: 'PR-2026-09-008',
    itemId: 'itm-011',
    itemCode: 'ITM-011',
    itemName: 'Kain Denim Stretch 12oz Indigo',
    category: 'Raw Materials',
    condition: 'Grade A (QC Passed - OEKO-TEX)',
    conditionNotes: 'Kain denim stretch lebar 148cm grade ekspor, lolos uji shrinkage & color fastness',
    batchNumber: 'BATCH-202609-DN01',
    warehouseLocation: 'Gudang Utama - Rak Kain A-03',
    status: 'AVAILABLE',
    initialQty: 800,
    remainingQty: 800,
    unitCost: 45000,
    uomCode: 'METER',
    supplierName: 'PT Mitra Sukses Industri',
    vendorDeliveryNote: 'SJ/MSI/2026/09/088',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-15T11:00:00.000Z',
  },
  {
    id: 'INV-1789500000002',
    inventoryId: 'INV-1789500000002',
    grItemId: 'gri-002-2',
    grId: 'gr-002',
    grNumber: 'GR-2026-09-002',
    poId: 'po-003',
    poNumber: 'PO-2026-09-003',
    prNumber: 'PR-2026-09-008',
    itemId: 'itm-010',
    itemCode: 'ITM-010',
    itemName: 'Resleting / Zipper YKK Metal 5 inch',
    category: 'Accessories',
    condition: 'Grade A (QC Passed)',
    conditionNotes: 'Resleting YKK metal kuningan antik 5 inch, closed end, fungsi lancar',
    batchNumber: 'BATCH-202609-ZP02',
    warehouseLocation: 'Gudang Utama - Rak Aksesoris B-02',
    status: 'AVAILABLE',
    initialQty: 600,
    remainingQty: 600,
    unitCost: 4200,
    uomCode: 'PCS',
    supplierName: 'PT Mitra Sukses Industri',
    vendorDeliveryNote: 'SJ/MSI/2026/09/088',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-15T11:00:00.000Z',
  },
  {
    id: 'INV-1789500000003',
    inventoryId: 'INV-1789500000003',
    grItemId: 'gri-002-3',
    grId: 'gr-002',
    grNumber: 'GR-2026-09-002',
    poId: 'po-003',
    poNumber: 'PO-2026-09-003',
    prNumber: 'PR-2026-09-005',
    itemId: 'itm-014',
    itemCode: 'ITM-014',
    itemName: 'Label Woven Satin Brand PT XYZ',
    category: 'Accessories',
    condition: 'Grade A (QC Passed)',
    conditionNotes: 'Label woven satin damask 25x55mm, kualitas ekspor, laser cut rapi',
    batchNumber: 'BATCH-202609-LB01',
    warehouseLocation: 'Gudang Utama - Bin Aksesoris C-01',
    status: 'AVAILABLE',
    initialQty: 3000,
    remainingQty: 3000,
    unitCost: 350,
    uomCode: 'PCS',
    supplierName: 'PT Mitra Sukses Industri',
    vendorDeliveryNote: 'SJ/MSI/2026/09/088',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-15T11:00:00.000Z',
  },
  {
    id: 'INV-1789500000004',
    inventoryId: 'INV-1789500000004',
    grItemId: 'gri-003-1',
    grId: 'gr-003',
    grNumber: 'GR-2026-09-003',
    poId: 'po-004',
    poNumber: 'PO-2026-09-004',
    prNumber: 'PR-2026-09-007',
    itemId: 'itm-016',
    itemCode: 'ITM-016',
    itemName: 'Jarum Mesin Jahit Groz-Beckert DPx5 #14',
    category: 'Consumables',
    condition: 'Grade A (Original Germany)',
    conditionNotes: 'Jarum jahit presisi original tersegel isi 10 batang per box',
    batchNumber: 'BATCH-202609-JR01',
    warehouseLocation: 'Gudang Sparepart - Rak M-01',
    status: 'AVAILABLE',
    initialQty: 25,
    remainingQty: 25,
    unitCost: 85000,
    uomCode: 'BOX',
    supplierName: 'CV Sumber Logistik Mandiri',
    vendorDeliveryNote: 'SJ/SLM/2026/09/145',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-15T15:00:00.000Z',
  },
  {
    id: 'INV-1789500000005',
    inventoryId: 'INV-1789500000005',
    grItemId: 'gri-003-2',
    grId: 'gr-003',
    grNumber: 'GR-2026-09-003',
    poId: 'po-004',
    poNumber: 'PO-2026-09-004',
    prNumber: 'PR-2026-09-007',
    itemId: 'itm-019',
    itemCode: 'ITM-019',
    itemName: 'Sarung Tangan Kerja Katun Rajut Bintik',
    category: 'Consumables',
    condition: 'Grade A (QC Passed - 100 Pasang)',
    conditionNotes: 'Sarung tangan katun bintik PVC anti slip K3 (2 pasang cacat telah disisihkan di reject log)',
    batchNumber: 'BATCH-202609-ST01',
    warehouseLocation: 'Gudang APD & K3 - Rak D-04',
    status: 'AVAILABLE',
    initialQty: 100,
    remainingQty: 100,
    unitCost: 6500,
    uomCode: 'PASANG',
    supplierName: 'CV Sumber Logistik Mandiri',
    vendorDeliveryNote: 'SJ/SLM/2026/09/145',
    receiverName: 'Agus Wibowo',
    createdAt: '2026-09-15T15:00:00.000Z',
  },
];

let grCounter = 1;
let txCounter = 2;
let costLayerCounter = 3;
let lastInventoryTimestamp = 0;

export function generateInventoryId(): string {
  let now = Date.now();
  if (now <= lastInventoryTimestamp) {
    now = lastInventoryTimestamp + 1;
  }
  lastInventoryTimestamp = now;
  return `INV-${now}`;
}

export function generateGRNumber(): string {
  grCounter += 1;
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const seq = String(grCounter).padStart(3, '0');
  return `GR-${year}-${month}-${seq}`;
}

export function createInitialGoodsReceiptForPO(po: PORecord): GoodsReceiptRecord {
  const existing = grDatabase.find((g) => g.poId === po.id || g.poNumber === po.poNumber);
  if (existing) return existing;

  const totalReceived = po.items.reduce((s, it) => s + (it.receivedQty || 0), 0);
  const totalOrdered = po.items.reduce((s, it) => s + (it.orderedQty || 0), 0);
  const isCompleted = po.status === POStatus.COMPLETED || (totalOrdered > 0 && totalReceived >= totalOrdered);

  let initialStatus = 'WAITING_DELIVERY';
  if (isCompleted) {
    initialStatus = 'COMPLETED';
  } else if (po.status === POStatus.CANCELLED) {
    initialStatus = totalReceived > 0 ? 'COMPLETED' : 'CANCELLED';
  } else if (totalReceived > 0) {
    initialStatus = 'PARTIALLY_RECEIVED';
  }

  const grId = `gr-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  const grNumber = generateGRNumber();
  const nowIso = new Date().toISOString();

  const items: GoodsReceiptItemRecord[] = po.items.map((it, idx) => ({
    id: `gri-${grId}-${idx + 1}`,
    grId,
    poItemId: it.id,
    itemId: it.itemId,
    itemCode: it.itemCode,
    itemName: it.itemName,
    receivedQty: it.receivedQty || 0,
    rejectedQty: 0,
    uomId: it.uomId,
    uomCode: it.uomCode,
    unitCost: it.unitPrice,
    conditionNotes: '',
    createdAt: nowIso,
  }));

  const initialGR: GoodsReceiptRecord = {
    id: grId,
    grNumber,
    vendorDeliveryNote: '-',
    receiveDate: nowIso,
    poId: po.id,
    poNumber: po.poNumber,
    supplierId: po.supplierId,
    supplierName: po.supplierSnapshot.name,
    receiverId: 'usr-wh-01',
    receiverName: 'Agus Wibowo',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    status: initialStatus as any,
    notes: isCompleted ? `Penerimaan selesai untuk PO ${po.poNumber}` : `Menunggu kedatangan kiriman barang untuk PO ${po.poNumber}`,
    items,
    totalGoodQty: totalReceived,
    totalRejectedQty: 0,
    createdAt: nowIso,
    updatedAt: nowIso,
  };

  grDatabase.unshift(initialGR);
  savePersistentData('gr-database.json', grDatabase);
  return initialGR;
}

export function syncGRStatusOnPOCompleted(poId: string) {
  const gr = grDatabase.find((g) => g.poId === poId);
  if (!gr) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gr.status = 'COMPLETED' as any;
  gr.updatedAt = new Date().toISOString();
  savePersistentData('gr-database.json', grDatabase);
}

export function syncGRStatusOnPOCancel(poId: string, totalReceived: number) {
  const gr = grDatabase.find((g) => g.poId === poId);
  if (!gr) return;
  if (totalReceived > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gr.status = 'COMPLETED' as any;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gr.status = 'CANCELLED' as any;
  }
  gr.updatedAt = new Date().toISOString();
  savePersistentData('gr-database.json', grDatabase);
}

registerPOIssuedListener(createInitialGoodsReceiptForPO);
registerPOCancelListener(syncGRStatusOnPOCancel);
registerPOCompletedListener(syncGRStatusOnPOCompleted);

// Ensure all active ISSUED POs have an initial GR for warehouse visibility
for (const po of poDatabase) {
  if (
    (po.status === POStatus.ISSUED || po.status === POStatus.PARTIALLY_RECEIVED) &&
    !grDatabase.some((g) => g.poId === po.id || g.poNumber === po.poNumber)
  ) {
    createInitialGoodsReceiptForPO(po);
  }
}

// Auto-reconcile GR statuses with their PO receiving state
for (const gr of grDatabase) {
  const linkedPO = poDatabase.find((p) => p.id === gr.poId || p.poNumber === gr.poNumber);
  if (linkedPO) {
    const totalReceived = linkedPO.items.reduce((s, it) => s + (it.receivedQty || 0), 0);
    const totalOrdered = linkedPO.items.reduce((s, it) => s + (it.orderedQty || 0), 0);
    if (linkedPO.status === POStatus.CANCELLED) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gr.status = totalReceived > 0 ? ('COMPLETED' as any) : ('CANCELLED' as any);
    } else if (linkedPO.status === POStatus.COMPLETED || (totalOrdered > 0 && totalReceived >= totalOrdered)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gr.status = 'COMPLETED' as any;
    } else if (totalReceived > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gr.status = 'PARTIALLY_RECEIVED' as any;
    }
  }
}
savePersistentData('gr-database.json', grDatabase);

export function getGoodsReceipts(filters?: {
  poNumber?: string;
  grNumber?: string;
  supplier?: string;
  deliveryNote?: string;
  date?: string;
  search?: string;
}): GoodsReceiptRecord[] {
  let list = [...grDatabase];

  if (!filters) return list.sort((a, b) => new Date(b.receiveDate).getTime() - new Date(a.receiveDate).getTime());

  if (filters.poNumber) {
    const q = filters.poNumber.toLowerCase().trim();
    list = list.filter((g) => g.poNumber.toLowerCase() === q || g.poId === filters.poNumber);
  }
  if (filters.grNumber) {
    const q = filters.grNumber.toLowerCase();
    list = list.filter((g) => g.grNumber.toLowerCase().includes(q));
  }
  if (filters.supplier) {
    const q = filters.supplier.toLowerCase();
    list = list.filter((g) => g.supplierName.toLowerCase().includes(q));
  }
  if (filters.deliveryNote) {
    const q = filters.deliveryNote.toLowerCase();
    list = list.filter((g) => g.vendorDeliveryNote.toLowerCase().includes(q));
  }
  if (filters.date) {
    list = list.filter((g) => g.receiveDate.startsWith(filters.date!));
  }
  if (filters.search) {
    const q = filters.search.toLowerCase();
    list = list.filter(
      (g) =>
        g.grNumber.toLowerCase().includes(q) ||
        g.poNumber.toLowerCase().includes(q) ||
        g.vendorDeliveryNote.toLowerCase().includes(q) ||
        g.supplierName.toLowerCase().includes(q) ||
        g.receiverName.toLowerCase().includes(q)
    );
  }

  return list.sort((a, b) => new Date(b.receiveDate).getTime() - new Date(a.receiveDate).getTime());
}

export function getGoodsReceiptById(id: string): (GoodsReceiptRecord & {
  po: PORecord | undefined;
  auditLogs: PRAuditRecord[];
  transactions: InventoryTransactionRecord[];
  costLayers: InventoryCostLayerRecord[];
}) | undefined {
  const gr = grDatabase.find((g) => g.id === id || g.grNumber === id);
  if (!gr) return undefined;

  const po = poDatabase.find((p) => p.id === gr.poId);
  const auditLogs = auditLogsDatabase
    .filter((a) => a.entityId === gr.id)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const transactions = inventoryTransactionDatabase.filter((t) => t.grId === gr.id);
  const costLayers = inventoryCostLayerDatabase.filter((c) => c.grId === gr.id);

  return {
    ...gr,
    po,
    auditLogs,
    transactions,
    costLayers,
  };
}

/**
 * Executes an atomic Goods Receipt receiving workflow.
 * Sacred Invariants:
 * 1. Over-delivery strictly forbidden (goodQty <= outstandingQty)
 * 2. PO does not increase inventory; only Goods Receipt increases inventory
 * 3. Historical purchase price preserved in InventoryCostLayer (unitCost = poItem.unitPrice)
 * 4. Good quantity increases inventory and cost layers; rejected quantity recorded for vendor audit
 * 5. Atomic transaction boundary: if any check or step fails, all mutations are rolled back.
 */
export function receiveGoods(
  payload: ReceiveGoodsPayload,
  receiver: { id: string; name: string; role: string }
): GoodsReceiptRecord {
  // 1. Locate PO
  const po = poDatabase.find((p) => p.id === payload.poId || p.poNumber === payload.poId);
  if (!po) {
    throw new Error(`Purchase Order '${payload.poId}' not found.`);
  }

  // 2. Enforce PO status in ['ISSUED', 'PARTIALLY_RECEIVED']
  if (po.status !== POStatus.ISSUED && po.status !== POStatus.PARTIALLY_RECEIVED) {
    throw new Error(
      `Cannot receive goods against Purchase Order '${po.poNumber}' with status '${po.status}'. Goods can only be received against ISSUED or PARTIALLY_RECEIVED orders.`
    );
  }

  // 3. Validate Delivery Note
  const deliveryNote = (payload.vendorDeliveryNote || '').trim();
  if (!deliveryNote) {
    throw new Error('Vendor Delivery Note (Surat Jalan) is mandatory for warehouse receiving.');
  }

  // 4. Validate items array
  if (!Array.isArray(payload.items) || payload.items.length === 0) {
    throw new Error('At least one line item is required to record a Goods Receipt.');
  }

  // 5. Pre-validate all line items before making ANY mutations (Strict Invariant Protection)
  const validatedLines: Array<{
    poItem: POItemRecord;
    goodQty: number;
    rejectedQty: number;
    conditionNotes?: string;
  }> = [];

  let totalGoodAcrossLines = 0;
  let totalRejectedAcrossLines = 0;

  for (let idx = 0; idx < payload.items.length; idx++) {
    const input = payload.items[idx];
    const poItem = po.items.find((i) => i.id === input.poItemId);
    if (!poItem) {
      throw new Error(`Line #${idx + 1}: Purchase order item '${input.poItemId}' not found in PO ${po.poNumber}.`);
    }

    const goodQty = Number(input.goodReceivedQty ?? input.receivedQty ?? 0);
    const rejectedQty = Number(input.rejectedQty ?? 0);

    if (isNaN(goodQty) || goodQty < 0) {
      throw new Error(`Line #${idx + 1} (${poItem.itemCode}): Good received quantity cannot be negative.`);
    }
    if (isNaN(rejectedQty) || rejectedQty < 0) {
      throw new Error(`Line #${idx + 1} (${poItem.itemCode}): Rejected quantity cannot be negative.`);
    }

    // Sacred Invariant 1: Over-delivery strictly forbidden
    const check = validateGROverDelivery(poItem.orderedQty, poItem.receivedQty, goodQty);
    if (!check.valid) {
      throw new Error(
        `Over-delivery rejected for item ${poItem.itemCode} (${poItem.itemName}): incoming good quantity ${goodQty} exceeds outstanding quantity ${check.outstandingQty}. (Ordered: ${poItem.orderedQty}, Previously Received: ${poItem.receivedQty})`
      );
    }

    totalGoodAcrossLines += goodQty;
    totalRejectedAcrossLines += rejectedQty;

    validatedLines.push({
      poItem,
      goodQty,
      rejectedQty,
      conditionNotes: input.conditionNotes?.trim(),
    });
  }

  if (totalGoodAcrossLines === 0 && totalRejectedAcrossLines === 0) {
    throw new Error('Goods Receipt must contain at least one accepted or rejected item quantity.');
  }

  // --- ATOMIC TRANSACTION EXECUTION WITH ROLLBACK SAFETY ---
  const previousPoStatus = po.status;
  const previousItemQuantities = po.items.map((i) => ({ id: i.id, receivedQty: i.receivedQty }));

  try {
    // Check if an initial GR exists for this PO with status WAITING_DELIVERY
    const targetGR = grDatabase.find(
      (g) => (g.poId === po.id || g.poNumber === po.poNumber) && (g.status as string) === 'WAITING_DELIVERY'
    );

    const grId = targetGR ? targetGR.id : `gr-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const grNumber = targetGR ? targetGR.grNumber : generateGRNumber();
    const receiveDate = payload.receiveDate || new Date().toISOString();
    const nowIso = new Date().toISOString();

    const createdGrItems: GoodsReceiptItemRecord[] = [];
    const createdTransactions: InventoryTransactionRecord[] = [];
    const createdCostLayers: InventoryCostLayerRecord[] = [];

    for (let i = 0; i < validatedLines.length; i++) {
      const line = validatedLines[i];
      const grItemId = `gri-${grId}-${i + 1}`;

      let inventoryTxId: string | undefined = undefined;
      let costLayerId: string | undefined = undefined;

      // Only good received quantity creates inventory transactions & cost layers
      if (line.goodQty > 0) {
        txCounter += 1;
        inventoryTxId = `itx-${Date.now()}-${txCounter}`;

        costLayerCounter += 1;
        const inventoryId = generateInventoryId();
        costLayerId = inventoryId;

        createdTransactions.push({
          id: inventoryTxId,
          grId,
          grNumber,
          poId: po.id,
          poNumber: po.poNumber,
          itemId: line.poItem.itemId,
          itemCode: line.poItem.itemCode,
          itemName: line.poItem.itemName,
          quantity: line.goodQty,
          uomCode: line.poItem.uomCode,
          txType: 'PURCHASE_RECEIPT',
          txDate: receiveDate,
          createdAt: nowIso,
        });

        // Historical PO unit price preserved in cost layer with epoch millisecond inventoryId
        let itemCondition = 'Grade A (QC Passed)';
        if (line.conditionNotes && (line.conditionNotes.toLowerCase().includes('retak') || line.conditionNotes.toLowerCase().includes('defect') || line.conditionNotes.toLowerCase().includes('cacat') || line.conditionNotes.toLowerCase().includes('khusus'))) {
          itemCondition = 'Grade B (Kondisi Khusus)';
        }

        createdCostLayers.push({
          id: costLayerId,
          inventoryId,
          grItemId,
          grId,
          grNumber,
          poId: po.id,
          poNumber: po.poNumber,
          prNumber: line.poItem.prNumber,
          itemId: line.poItem.itemId,
          itemCode: line.poItem.itemCode,
          itemName: line.poItem.itemName,
          category: MASTER_ITEMS.find((m) => m.id === line.poItem.itemId)?.category || 'Packaging & Warehouse',
          condition: itemCondition,
          conditionNotes: line.conditionNotes || 'Penerimaan barang selesai dengan verifikasi QC.',
          batchNumber: `BATCH-${new Date(receiveDate).getFullYear()}${String(new Date(receiveDate).getMonth() + 1).padStart(2, '0')}-${line.poItem.itemCode}`,
          warehouseLocation: 'Gudang Utama - Area A',
          status: 'AVAILABLE',
          initialQty: line.goodQty,
          remainingQty: line.goodQty,
          unitCost: line.poItem.unitPrice,
          uomCode: line.poItem.uomCode,
          supplierName: po.supplierSnapshot.name,
          vendorDeliveryNote: deliveryNote,
          receiverName: receiver.name,
          createdAt: nowIso,
        });
      }

      // Mutate PO item receivedQty
      line.poItem.receivedQty += line.goodQty;

      createdGrItems.push({
        id: grItemId,
        grId,
        poItemId: line.poItem.id,
        itemId: line.poItem.itemId,
        itemCode: line.poItem.itemCode,
        itemName: line.poItem.itemName,
        receivedQty: line.goodQty,
        rejectedQty: line.rejectedQty,
        uomId: line.poItem.uomId,
        uomCode: line.poItem.uomCode,
        unitCost: line.poItem.unitPrice,
        conditionNotes: line.conditionNotes,
        inventoryTxId,
        costLayerId,
        createdAt: nowIso,
      });
    }

    // Determine new PO Status
    const newPoStatus = calculatePOReceiptStatus(po.items);
    po.status = newPoStatus;
    po.updatedAt = nowIso;

    const totalOutstandingAfterThis = po.items.reduce(
      (sum, it) => sum + Math.max(0, it.orderedQty - (it.receivedQty || 0)),
      0
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const grStatus = totalOutstandingAfterThis === 0 ? GRStatus.CONFIRMED : ('PARTIALLY_RECEIVED' as any);

    let recordedGR: GoodsReceiptRecord;

    if (targetGR) {
      targetGR.vendorDeliveryNote = deliveryNote;
      targetGR.receiveDate = receiveDate;
      targetGR.receiverId = receiver.id;
      targetGR.receiverName = receiver.name;
      targetGR.status = grStatus;
      targetGR.notes = payload.notes?.trim() || targetGR.notes;
      targetGR.items = createdGrItems;
      targetGR.totalGoodQty = totalGoodAcrossLines;
      targetGR.totalRejectedQty = totalRejectedAcrossLines;
      targetGR.updatedAt = nowIso;
      recordedGR = targetGR;
    } else {
      recordedGR = {
        id: grId,
        grNumber,
        vendorDeliveryNote: deliveryNote,
        receiveDate,
        poId: po.id,
        poNumber: po.poNumber,
        supplierId: po.supplierId,
        supplierName: po.supplierSnapshot.name,
        receiverId: receiver.id,
        receiverName: receiver.name,
        status: grStatus,
        notes: payload.notes?.trim(),
        items: createdGrItems,
        totalGoodQty: totalGoodAcrossLines,
        totalRejectedQty: totalRejectedAcrossLines,
        createdAt: nowIso,
        updatedAt: nowIso,
      };
      grDatabase.unshift(recordedGR);
    }

    savePersistentData('gr-database.json', grDatabase);
    syncGRToDatabase(recordedGR);
    inventoryTransactionDatabase.unshift(...createdTransactions);
    inventoryCostLayerDatabase.unshift(...createdCostLayers);

    // Record Goods Receipt Audit Log
    addAuditLog({
      entityType: 'GOODS_RECEIPT',
      entityId: recordedGR.id,
      fromStatus: null,
      toStatus: grStatus,
      actorId: receiver.id,
      actorName: receiver.name,
      actorRole: receiver.role,
      action: 'CONFIRM_GOODS_RECEIPT',
      reason: `Penerimaan fisik barang untuk PO ${po.poNumber} berdasarkan Surat Jalan ${deliveryNote}. Diterima bagus: ${totalGoodAcrossLines} unit, Reject: ${totalRejectedAcrossLines} unit.`,
      metadata: {
        grNumber: recordedGR.grNumber,
        poNumber: po.poNumber,
        deliveryNote,
        totalGoodQty: totalGoodAcrossLines,
        totalRejectedQty: totalRejectedAcrossLines,
        transactionsCount: createdTransactions.length,
        costLayersCount: createdCostLayers.length,
      },
    });

    // Record PO Receiving Progress Audit Log
    addAuditLog({
      entityType: 'PURCHASE_ORDER',
      entityId: po.id,
      fromStatus: previousPoStatus,
      toStatus: newPoStatus,
      actorId: receiver.id,
      actorName: receiver.name,
      actorRole: receiver.role,
      action: newPoStatus === POStatus.COMPLETED ? 'PO_RECEIPT_COMPLETED' : 'PO_PARTIALLY_RECEIVED',
      reason:
        newPoStatus === POStatus.COMPLETED
          ? `Seluruh barang pesanan pada PO ${po.poNumber} telah diterima lengkap di gudang (Surat Jalan: ${deliveryNote}).`
          : `Penerimaan bertahap dicatat untuk PO ${po.poNumber} (Surat Jalan: ${deliveryNote}). Sisa barang masih menunggu pengiriman berikutnya.`,
      metadata: {
        grNumber: recordedGR.grNumber,
        poNumber: po.poNumber,
        previousPoStatus,
        newPoStatus,
      },
    });

    return recordedGR;
  } catch (err) {
    // Atomic Rollback: Restore PO state if any step fails
    po.status = previousPoStatus;
    for (const snap of previousItemQuantities) {
      const item = po.items.find((i) => i.id === snap.id);
      if (item) item.receivedQty = snap.receivedQty;
    }
    throw err;
  }
}

/**
 * Returns current stock summary and total valuation per item.
 */
export function getInventoryStockSummary(): ItemStockSummaryRecord[] {
  // Catalog of items
  const itemMap = new Map<
    string,
    {
      itemId: string;
      itemCode: string;
      itemName: string;
      category: string;
      uomCode: string;
      currentStock: number;
      totalValue: number;
      layerCount: number;
    }
  >();

  // Initialize from Master Items
  for (const master of MASTER_ITEMS) {
    itemMap.set(master.id, {
      itemId: master.id,
      itemCode: master.code,
      itemName: master.name,
      category: master.category,
      uomCode: master.uomCode,
      currentStock: 0,
      totalValue: 0,
      layerCount: 0,
    });
  }

  // Aggregate active cost layers
  for (const layer of inventoryCostLayerDatabase) {
    const existing = itemMap.get(layer.itemId) || {
      itemId: layer.itemId,
      itemCode: layer.itemCode,
      itemName: layer.itemName,
      category: 'General',
      uomCode: layer.uomCode,
      currentStock: 0,
      totalValue: 0,
      layerCount: 0,
    };

    existing.currentStock += layer.remainingQty;
    existing.totalValue += layer.remainingQty * layer.unitCost;
    if (layer.remainingQty > 0) {
      existing.layerCount += 1;
    }
    itemMap.set(layer.itemId, existing);
  }

  return Array.from(itemMap.values()).sort((a, b) => {
    // Items with stock first, then by code
    if (b.currentStock !== a.currentStock) return b.currentStock - a.currentStock;
    return a.itemCode.localeCompare(b.itemCode);
  });
}

/**
 * Returns FIFO cost layers, optionally filtered by itemId.
 */
export function getInventoryCostLayers(itemId?: string): InventoryCostLayerRecord[] {
  let list = [...inventoryCostLayerDatabase];
  if (itemId) {
    list = list.filter((l) => l.itemId === itemId || l.itemCode === itemId);
  }
  // FIFO order: created oldest first
  return list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

/**
 * Returns inventory transaction log, optionally filtered by itemId.
 */
export function getInventoryTransactions(itemId?: string): InventoryTransactionRecord[] {
  let list = [...inventoryTransactionDatabase];
  if (itemId) {
    list = list.filter((t) => t.itemId === itemId || t.itemCode === itemId);
  }
  // Most recent first
  return list.sort((a, b) => new Date(b.txDate).getTime() - new Date(a.txDate).getTime());
}

/**
 * Returns a specific inventory stock item by its ID or Inventory ID.
 */
export function getInventoryItemById(id: string): InventoryCostLayerRecord | undefined {
  const target = id.toLowerCase().trim();
  return inventoryCostLayerDatabase.find(
    (l) =>
      l.id.toLowerCase() === target ||
      l.inventoryId.toLowerCase() === target ||
      l.grItemId.toLowerCase() === target
  );
}

export function getGRAuditLogs(grId: string): PRAuditRecord[] {
  return auditLogsDatabase
    .filter((log) => log.entityId === grId)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
